import React, {useEffect, useState, useCallback, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {chatMessagesAction} from "../../../actions/chatAction";
import {Loader} from "../../../utils/loader/Loader";
import {curatorsAction} from "../../../actions/usersAction";
import {msgDirectionClientToUser, msgDirectionSystemToUser, userPrintName, newAiUiEnabled} from "../../../constants";
import {ChatMessage} from "./ChatMessage";
import {ChatAdd} from "./ChatAdd";
import {ChatAiCard} from "./ChatAiCard";

const ChatMessages = (props) => {
  const [limit] = useState(100);
  const [users, setUsers] = useState({});
  const [scrolling, setScrolling] = useState(0);
  const [unread, setUnread] = useState(0);
  const [typeAutoMessageBoHide, setTypeAutoMessageBoHide] = useState(true);

  const chatRef = useRef(null);
  const scrollPos = useRef({height: 0, prev: 0, offset: 0, manual: false});
  const dispatch = useDispatch();
  const clientID = useSelector(state => state.chat.clientID);
  const chatID = useSelector(state => state.chat.chatID);
  const messages = useSelector(state => state.chat.messages);
  // Primitives, not the client object: it is recreated on every UPDATE_CHAT_CLIENT_DATA
  // (block, archive, channel change, health save) and would rerender the whole message feed.
  const clientId = useSelector(state => state.chat.client ? state.chat.client.id : null);
  const clientAiEnabled = useSelector(state => !!(state.chat.client && state.chat.client.enable_ai));
  const userAiEnabled = useSelector(state => !!(state.user.profile && state.user.profile.enable_ai));
  const aiBatch = useSelector(state => state.chat.aiBatch);
  const offset = useSelector(state => state.chat.messages_offset);
  const total = useSelector(state => state.chat.messages_total);
  const loading = useSelector(state => state.chat.messages_loading);

  const getMessages = useCallback((clients_id, chat_id, offset, limit, type_auto_message_bot_hide) => {
    dispatch(chatMessagesAction({clients_id, chat_id, offset, limit, type_auto_message_bot_hide}));
  }, [dispatch]);

  const handleScroll = useCallback(() => {
    if (!scrollPos.current.manual && chatRef && chatRef.current) {
      if (chatRef.current.scrollTop <= 100 && !loading) {
        setScrolling(value => value === 0 && total > offset + limit ? offset + limit : value);
      }

      scrollPos.current = {
        height: chatRef.current.scrollHeight,
        prev: chatRef.current.scrollHeight,
        offset: chatRef.current.scrollTop || 1,
        manual: false
      };
    }
  }, [offset, limit, total, loading]);

  useEffect(() => {
    if (!clientID) {
      return;
    }

    //setTypeAutoMessageBoHide(props.hideMessagesCuratorFromBot);
    //console.log("props.hideMessagesCuratorFromBot:", props.hideMessagesCuratorFromBot)

    let typeAutoMessageBotHideTemp = true;

    let curHideBot = localStorage.getItem('hide_bot_' + clientID);

    if(curHideBot === '1'){
      typeAutoMessageBotHideTemp = false;
    }

    if (!loading && (offset === null || scrolling > offset)) {
      getMessages(clientID, chatID, scrolling, limit, typeAutoMessageBotHideTemp);
    }
  }, [clientID, chatID, scrolling, offset, limit, loading, getMessages]);

  useEffect(() => {
    if (offset === null) {
      scrollPos.current = {height: 0, prev: 0, offset: 0, manual: false};
      setScrolling(0);
    }
  }, [offset]);

  useEffect(() => {
    if (loading === false) {
      setScrolling(0);
    }
  }, [loading]);

  useEffect(() => {
    scrollPos.current.manual = true;
    const tm = setInterval(() => {
      if (!chatRef || !chatRef.current) {
        clearInterval(tm);
        return;
      }
      if (scrollPos.current.offset === 0) {
        chatRef.current.scrollTop = chatRef.current.scrollHeight - chatRef.current.clientHeight;
      } else {
        chatRef.current.scrollTop = chatRef.current.scrollHeight - scrollPos.current.prev + scrollPos.current.offset;
      }
      if (scrollPos.current.height === chatRef.current.scrollHeight) {
        scrollPos.current.manual = false;
        clearInterval(tm);
      }
      scrollPos.current.height = chatRef.current.scrollHeight;
    }, 50);

    return () => {
      scrollPos.current.manual = false;
      clearInterval(tm);
    }

  }, [messages.length]);

  // The suggestion card arrives asynchronously, after the messages. If the curator is near
  // the bottom, scroll down so the card becomes visible. Kept separate from pagination,
  // which preserves the scroll position.
  useEffect(() => {
    if (!aiBatch || !chatRef.current) {
      return;
    }
    const el = chatRef.current;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 400;
    if (nearBottom) {
      requestAnimationFrame(() => {
        if (chatRef.current) {
          chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
      });
    }
  }, [aiBatch]);

  useEffect(() => {
    setUnread(messages.reduce((res, i) => {
      if ((i.message_direction === msgDirectionClientToUser || i.message_direction === msgDirectionSystemToUser) && !i.read_status) {
        res += 1;
      }
      return res;
    }, 0));
  }, [messages, messages.length]);

  useEffect(() => {
    if (!chatRef || !chatRef.current) {
      return;
    }
    const elem = chatRef.current;
    elem.addEventListener('scroll', handleScroll);
    return () => elem.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    dispatch(curatorsAction()).then((list) => {
      setUsers((list || []).reduce((res, i) => {
        res[i.id] = {...i, print_name: userPrintName(i)};
        return res;
      }, {}));
    }).catch(() => {

    });
  }, [dispatch]);

  return <div className="chat">
            <div className="chat__wrap" ref={chatRef}>
                <div className="chat__wrapper">
                    {loading && <Loader size="large" padded/>}
                    {
                        messages.length > 0 && messages.map((i, k) => {
                            i.hideBotMsg = true;
                            return <ChatMessage hideMessagesFromBot={props.hideMessagesFromBot}
                                                hideMessagesCuratorFromBot={props.hideMessagesCuratorFromBot}
                                                key={k} prev={k > 0 ? messages[k - 1] : null}
                                                item={i}
                                                curator={i.users_id && users[i.users_id] ? users[i.users_id] : null}/>;
                        })
                    }
                    {newAiUiEnabled() && chatID && clientId && clientAiEnabled && userAiEnabled &&
                      <ChatAiCard clientId={clientId} chatID={chatID}/>}
                </div>
            </div>
            {clientID && !loading && <ChatAdd unreadCnt={unread}/>}
        </div>;
};

export default ChatMessages
